/**
 * Global UpsellModal — mounted ONCE at the App root, subscribes to
 * `onUpsellEvent` and pops a contextual paywall whenever the axios
 * interceptor catches a 402 "feature_not_entitled".
 *
 * iter-170ad: now uses ``RecommendationCards`` so the user sees the
 * concrete plan/addon/pack options for the gated feature instead of a
 * generic "view plans" CTA.
 */
import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Sparkles, Lock } from "lucide-react";
import { onUpsellEvent } from "../contexts/UpsellEvents";
import { useLang } from "../contexts/LangContext";
import { tx } from "../i18n";
import { RecommendationCards } from "./RecommendationCards";
import { useFeatureRecommendation } from "../hooks/useFeatureRecommendation";

export function UpsellModal() {
  const { lang } = useLang();
  const [payload, setPayload] = useState(null);
  // Pre-fetch as soon as the modal payload arrives so the recommendation
  // cards render without a visible loading flash.
  const { data: rec } = useFeatureRecommendation(payload?.feature_key);

  useEffect(() => {
    return onUpsellEvent((p) => setPayload(p));
  }, []);

  if (!payload) return null;
  const featureKey = payload.feature_key;
  // Title: catalog label when available, fallback to the raw key so we
  // never render an empty header even for newly-introduced features.
  const title = tx(
    lang,
    rec?.feature?.label_en || featureKey,
    rec?.feature?.label_es || featureKey,
    rec?.feature?.label_pt || featureKey,
  );

  return (
    <Dialog open={!!payload} onOpenChange={(o) => !o && setPayload(null)}>
      <DialogContent className="max-w-2xl" data-testid="upsell-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <div className="h-9 w-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
              <Lock className="h-4 w-4" />
            </div>
            <span className="font-serif-display">
              {tx(lang, "PRO Feature", "Función PRO", "Funcionalidade PRO")}
            </span>
          </DialogTitle>
          <DialogDescription className="pt-2">
            <strong className="text-slate-900">{title}</strong>
            <br />
            <span className="text-slate-600 text-sm">
              {tx(lang,
                "Your current plan doesn't include this feature.",
                "Tu plan actual no incluye esta funcionalidad.",
                "Seu plano atual não inclui esta funcionalidade.")}
            </span>
          </DialogDescription>
        </DialogHeader>

        {/* iter-170ad — Concrete upgrade paths instead of a generic CTA. */}
        <div className="space-y-2">
          <p className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wider flex items-center gap-1">
            <Sparkles className="h-3 w-3" />
            {tx(lang, "Unlock with", "Activala con", "Ative com")}
          </p>
          <RecommendationCards featureKey={featureKey} />
        </div>

        <div className="flex justify-end pt-2">
          <Button
            variant="ghost"
            onClick={() => setPayload(null)}
            data-testid="upsell-modal-dismiss"
          >
            {tx(lang, "Later", "Más tarde", "Mais tarde")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
